import type { RvtElement } from '@manudev/rvt';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useSelectionStore } from '@/state/selection';
import { RevitCard } from './Card';
import { elementFocus, RVT_LAYER } from './layer';
import { useRvtStore } from './rvt';

const mb = (bytes: number) => `${(bytes / 1e6).toFixed(1)} MB`;
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
const readable = (s: string) => (/[A-Za-z0-9]/.test(s) ? s : '');
const label = (e: RvtElement) => readable(e.name) || readable(e.typeName) || `#${e.id}`;

function useElapsed(running: boolean, since: number): number {
  const [now, setNow] = useState(since);
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNow(performance.now()), 500);
    return () => clearInterval(id);
  }, [running]);
  return Math.max(0, now - since);
}

export function RevitPanel() {
  const status = useRvtStore((s) => s.status);
  const file = useRvtStore((s) => s.file);
  const info = useRvtStore((s) => s.info);
  const elements = useRvtStore((s) => s.elements);
  const levels = useRvtStore((s) => s.levels);
  const records = useRvtStore((s) => s.records);
  const skipped = useRvtStore((s) => s.skipped);
  const stats = useRvtStore((s) => s.stats);
  const startedAt = useRvtStore((s) => s.startedAt);
  const loadMs = useRvtStore((s) => s.loadMs);
  const error = useRvtStore((s) => s.error);
  const exporting = useRvtStore((s) => s.exporting);
  const open = useRvtStore((s) => s.open);
  const loadSample = useRvtStore((s) => s.loadSample);
  const downloadGlb = useRvtStore((s) => s.downloadGlb);
  const context = useRvtStore((s) => s.context);
  const setContext = useRvtStore((s) => s.setContext);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const busy = status === 'reading' || status === 'meshing';
  const elapsed = useElapsed(busy, startedAt);

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    const f = e.dataTransfer.files[0];
    if (f) open(f);
  };

  return (
    <>
      <RevitCard />
      <div
        className="lens-panel revit"
        data-over={over}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <div className="lens-bar">
          <span className="lens-dot" />
          <span>open revit</span>
          <span className="lens-right">
            {busy
              ? `${status === 'reading' ? 'reading' : 'geometry'}… ${Math.round(elapsed / 1000)} s`
              : status === 'ready' && loadMs != null
                ? `read in ${seconds(loadMs)}`
                : status === 'error'
                  ? 'failed'
                  : '–'}
          </span>
        </div>
        <div className="rvt-drop">
          <span>Drop a .rvt or .rfa on this card, or</span>
          <button type="button" onClick={() => input.current?.click()}>
            choose a file
          </button>
          <button type="button" onClick={loadSample} disabled={busy}>
            try the sample
          </button>
          <input
            ref={input}
            type="file"
            accept=".rvt,.rfa"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) open(f);
              e.target.value = '';
            }}
          />
        </div>
        {status === 'error' && <p className="hint">could not decode: {error}</p>}
        {info && <FileInfo />}
        {info && info.support !== 'full' && <p className="hint">{info.supportNote}</p>}
        {info?.support === 'full' && (
          <>
            <dl className="lens-stats">
              <div>
                <dt>elements</dt>
                <dd>{elements.length || '–'}</dd>
              </div>
              <div>
                <dt>solids</dt>
                <dd>{stats?.solids ?? '–'}</dd>
              </div>
              <div>
                <dt>boxes</dt>
                <dd>{stats?.boxes ?? '–'}</dd>
              </div>
              <div>
                <dt>records</dt>
                <dd>{records ? records.toLocaleString('en') : '–'}</dd>
              </div>
            </dl>
            {levels.length > 0 && (
              <p className="rvt-levels">
                {levels.map((l) => (
                  <span key={l.id}>
                    {l.name} <b>{l.elevation == null ? '–' : `${l.elevation.toFixed(2)} m`}</b>
                  </span>
                ))}
              </p>
            )}
            <label className="rvt-context">
              <input
                type="checkbox"
                checked={context}
                onChange={(e) => setContext(e.target.checked)}
              />
              show terrain, planting and rooms
            </label>
            <ElementTree elements={elements} />
            {skipped.length > 0 && <SkippedLine skipped={skipped} />}
            {status === 'ready' && stats && stats.solids + stats.boxes > 0 && (
              <button type="button" className="action" onClick={downloadGlb} disabled={exporting}>
                {exporting ? 'Writing GLB…' : 'Download the geometry (GLB)'}
              </button>
            )}
            {status === 'ready' && error && <p className="hint">GLB failed: {error}</p>}
          </>
        )}
        {!info && busy && <p className="hint">Reading {file?.name ?? 'the file'}…</p>}
        <p className="lens-foot">Built on phi-ag/rvt. Boxes stand in where I have no solid yet.</p>
      </div>
    </>
  );
}

function FileInfo() {
  const file = useRvtStore((s) => s.file);
  const info = useRvtStore((s) => s.info)!;
  const thumbnail = useRvtStore((s) => s.thumbnail);
  const rows: [string, string][] = [
    ['file', file?.name ?? '–'],
    ['revit', info.version || '–'],
    ['build', info.build || '–'],
    ['kind', info.family ? 'family' : 'project'],
    ['worksharing', info.worksharing ? 'workshared' : 'no'],
    ['size', file?.size ? mb(file.size) : '–'],
  ];
  return (
    <div className="rvt-info">
      {thumbnail ? (
        <img src={thumbnail} alt="Preview stored in the file" />
      ) : (
        <span className="rvt-thumb-none">no preview</span>
      )}
      <dl className="kv">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd title={v}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// Model elements by category, largest first, each category collapsed until opened.
function ElementTree({ elements }: { elements: RvtElement[] }) {
  const select = useSelectionStore((s) => s.select);
  const setFocus = useSelectionStore((s) => s.setFocus);
  const selected = useSelectionStore((s) => s.selected);
  const groups = useMemo(() => {
    const by = new Map<string, RvtElement[]>();
    for (const e of elements) {
      const list = by.get(e.category);
      if (list) list.push(e);
      else by.set(e.category, [e]);
    }
    return [...by].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
  }, [elements]);
  if (groups.length === 0) return null;

  const pick = (id: number) => {
    select({ layer: RVT_LAYER, expressID: id });
    const focus = elementFocus(id);
    if (focus) setFocus(focus);
  };

  return (
    <div className="rvt-tree">
      {groups.map(([category, list]) => (
        <details key={category}>
          <summary>
            <span>{category}</span>
            <span className="rvt-count">{list.length}</span>
          </summary>
          <ul>
            {list.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  aria-pressed={selected?.layer === RVT_LAYER && selected.expressID === e.id}
                  onClick={() => pick(e.id)}
                >
                  <span>{label(e)}</span>
                  <span className="rvt-meta">
                    {[label(e) === `#${e.id}` ? '' : `#${e.id}`, e.level]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

function SkippedLine({ skipped }: { skipped: { id: number; reason: string }[] }) {
  const reasons = new Map<string, number>();
  for (const s of skipped) reasons.set(s.reason, (reasons.get(s.reason) ?? 0) + 1);
  const text = [...reasons].map(([r, n]) => (reasons.size > 1 ? `${n} ${r}` : r)).join(', ');
  return (
    <p className="rvt-skipped">
      skipped: {skipped.length} element{skipped.length === 1 ? '' : 's'} ({text})
    </p>
  );
}
